import { getTime, format } from 'date-fns'
import { store } from '../store/index'

export function deleteSymbols(str) {
	if (str) {
		return str.replace(/[^A-Z0-9]/gi, '');
	}
	return ""
}

export function regexNumeric(str) {
	if (str) {
		return str.replace(/[^0-9]/gi, '');
	}
	return ""
}

export function formatMoneyInput(amount) {
	return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Mahsulot qabul: narx — faqat bitta nuqta yoki vergul (ikkisi birga emas); yolg‘iz `.` yoki `,` bo‘lmasin */
export function sanitizeDocumentInPriceInput(raw) {
	if (raw == null) return ''
	let v = String(raw).replace(/[^0-9.,]/g, '')
	const firstComma = v.indexOf(',')
	const firstDot = v.indexOf('.')
	if (firstComma !== -1 && firstDot !== -1) {
		if (firstComma < firstDot) {
			v = v.replace(/\./g, '')
			const fc = v.indexOf(',')
			v = v.slice(0, fc + 1) + v.slice(fc + 1).replace(/,/g, '')
			v = v.replace(',', '.')
		} else {
			v = v.replace(/,/g, '')
			const fd = v.indexOf('.')
			v = v.slice(0, fd + 1) + v.slice(fd + 1).replace(/\./g, '')
		}
	} else if (firstComma !== -1) {
		const fc = v.indexOf(',')
		v = v.slice(0, fc + 1) + v.slice(fc + 1).replace(/,/g, '')
		v = v.replace(',', '.')
	} else if (firstDot !== -1) {
		const fd = v.indexOf('.')
		v = v.slice(0, fd + 1) + v.slice(fd + 1).replace(/\./g, '')
	}
	if (v === '.' || v === ',') return ''
	return v
}

const DOCUMENTS_IN_MAX_PRICE = 1000000000

/** Mahsulot qabul formasi: tasdiqlashdan oldin — aniq xabarlar ro‘yxati (t — i18n t) */
export function collectDocumentsInProductErrors(productList, tableDocumentsInCreate, t) {
	const errors = []
	if (!productList?.length) return errors
	const bankRequired = !tableDocumentsInCreate || tableDocumentsInCreate?.bankPrice

	for (let i = 0; i < productList.length; i++) {
		const item = productList[i]
		const row = i + 1
		const name = String(item.name || item.productName || row).trim() || String(row)
		const prefix = t('DOCUMENT_IN_ERROR_ROW', { row, name })

		const hasSerial = item.serialNumbers?.length > 0
		if (!hasSerial) {
			// Miqdor 0 bo'lsa ham qabul qilinadi. Faqat kiritilmagan yoki noto'g'ri
			// (masalan "0.", ".", manfiy yoki raqam bo'lmagan belgilar) bo'lsa rad etiladi.
			const qStr = String(item.quantity ?? '').trim()
			if (qStr === '' || !/^\d+(\.\d+)?$/.test(qStr)) {
				errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_QUANTITY'))
			}
		}
		if (
			tableDocumentsInCreate?.packQuantity &&
			!item.serialNumbers?.length &&
			Object.prototype.hasOwnProperty.call(item, 'packQuantity')
		) {
			const pNum = Number(item.packQuantity)
			if (
				item.packQuantity === '' ||
				item.packQuantity == null ||
				!Number.isFinite(pNum) ||
				pNum <= 0
			) {
				errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_PACK_SIZE'))
			}
		}
		if (
			Number(item.price) > DOCUMENTS_IN_MAX_PRICE ||
			Number(item.salePrice) > DOCUMENTS_IN_MAX_PRICE ||
			Number(item.wholesalePrice) > DOCUMENTS_IN_MAX_PRICE ||
			Number(item.bankPrice) > DOCUMENTS_IN_MAX_PRICE
		) {
			errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_PRICE_MAX'))
		}
		if (item.price === '' || Number(item.price) < 0.0001) {
			errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_PRICE_MIN'))
		}
		if (item.wholesalePrice === '' || Number(item.wholesalePrice) < Number(item.price)) {
			errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_WHOLESALE'))
		}
		if (item.wholesalePrice === '' || Number(item.salePrice) < Number(item.wholesalePrice)) {
			errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_SALE'))
		}
		if (bankRequired) {
			if (item.bankPrice === '' || Number(item.bankPrice) < Number(item.price)) {
				errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_BANK'))
			}
		}
		if (item.vat === '') {
			errors.push(prefix + ' ' + t('DOCUMENT_IN_VALIDATE_VAT'))
		}
	}
	return errors
}

// Excel importini yuborishdan oldin "Shtrix kod" va "IKPU" ustunlarini tekshiradi:
// agar katakda raqamdan boshqa belgi (harf va h.k.) bo'lsa, qaysi qatorda va qaysi
// ustunda xatolik borligini qaytaradi. { errors: [{ rowNum, columnName, value, productName }] }
export async function validateImportFileBarcodes(file, XLSX) {
	const result = { errors: [] }
	if (!file || !XLSX) return result
	const isBarcodeHeader = (h) => [
		'shtrix kod', 'shtrix-kod', 'штрих код', 'штрих-код', 'barcode',
		'ikpu', 'икпу', 'mxik', 'мхик'
	].includes(h)
	const isNameHeader = (h) => [
		'mahsulot nomi', 'наименование товара', 'наименование', 'name of product', 'mahsulot'
	].includes(h)
	try {
		const buf = await file.arrayBuffer()
		const wb = XLSX.read(buf, { type: 'array' })
		const ws = wb.Sheets[wb.SheetNames[0]]
		if (!ws || !ws['!ref']) return result
		const range = XLSX.utils.decode_range(ws['!ref'])
		const headerRow = range.s.r

		const targetCols = []
		let nameCol = -1
		for (let c = range.s.c; c <= range.e.c; c++) {
			const cell = ws[XLSX.utils.encode_cell({ r: headerRow, c })]
			const raw = cell && cell.v != null ? String(cell.v).trim() : ''
			const h = raw.toLowerCase()
			if (isBarcodeHeader(h)) targetCols.push({ c, name: raw, normalizedName: h })
			if (nameCol === -1 && isNameHeader(h)) nameCol = c
		}
		if (!targetCols.length) return result

		for (let r = headerRow + 1; r <= range.e.r; r++) {
			for (const tc of targetCols) {
				const cell = ws[XLSX.utils.encode_cell({ r, c: tc.c })]
				if (!cell || cell.v == null || cell.v === '') continue
				const val = String(cell.v).replace(/\s+/g, '')
				if (val === '') continue
				const allowBarcodeList = ['shtrix kod', 'shtrix-kod', 'barcode'].includes(tc.normalizedName)
				const isValidValue = allowBarcodeList
					? /^\d+(,\d+)*$/.test(val)
					: /^\d+$/.test(val)
				if (!isValidValue) {
					let productName = ''
					if (nameCol >= 0) {
						const nc = ws[XLSX.utils.encode_cell({ r, c: nameCol })]
						productName = nc && nc.v != null ? String(nc.v).trim() : ''
					}
					result.errors.push({ rowNum: r + 1, columnName: tc.name, value: val, productName })
				}
			}
		}
	} catch (e) {
		// fayl o'qishda xato bo'lsa, backend o'zi tekshiradi
	}
	return result
}

export function returnSign(str) {
	return str.includes("?") ? '&' : '?'
}

export function regexPhoneNumber(str) {
	if (str) {
		return str.replace(/(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, "$1$2 $3-$4-$5");
	}
	return ""
}

export function clearTemporaryStorage() {
	localStorage.removeItem('tokenTime')
	localStorage.removeItem('authUser')
	localStorage.removeItem('username')
	localStorage.removeItem('authorities')
	localStorage.removeItem('access_token')
	localStorage.removeItem('role')
	// Sessiya kaliti: boshqa oynalar (tab) shu o'zgarishdan xabar topib chiqib ketadi.
	localStorage.removeItem('sessionKey')
	// Redux holati (`state`) ATAYLAB o'chirilmaydi: token muddati tugaganda yoki
	// brauzer yopilib qayta ochilganda ham shu funksiya chaqiriladi, holat esa
	// tugallanmagan hujjatlarni (documents-in va h.k.) saqlab turadi.
	// U faqat BOSHQA akkaunt kirganda tozalanadi -- store/localStorage.js: claimStateFor().
	sessionStorage.clear();
}

export function findFromArrayById(array, id) {
	if (array.length > 0 && id) {
		return array.filter(x => x.id === id)[0]?.name;
	}
}

export function findIdFromArray(array, id) {
	if (array.length > 0 && id) {
		if (array.filter(x => x.id === id).length > 0) {
			return array.filter(x => x.id === id)[0].id;
		} else {
			return false
		}
	}
}

export function formatMoney(amount, decimalCount = null, decimal = ".", thousands = " ") {
	if (!decimalCount && decimalCount !== 0) {
		const state = JSON.parse(localStorage.getItem('state'))
		if (state) {
			//decimalCount = state.settings.decimalPoint
			decimalCount = 2
		} else {
			decimalCount = 0
		}
	}

	try {
		decimalCount = Math.abs(decimalCount);
		decimalCount = isNaN(decimalCount) ? 0 : decimalCount; // 0 was 2

		const negativeSign = amount < 0 ? "-" : "";

		let i = parseInt(amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)).toString();
		let j = (i.length > 3) ? i.length % 3 : 0;

		return negativeSign + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) + (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : "");
	} catch (e) {
		console.log(e)
	}
}

/**
 * Excel eksport uchun: butun summalarda .00 tushmaydi, faqat minglik ajratgich (vergul) bilan butun son.
 * Kasrli bo'lsa kasr qismi ko'rsatiladi.
 */
export function formatMoneyForExcel(amount, thousands = ",") {
	const num = Number(amount);
	if (isNaN(num)) return '';
	const isWhole = Number.isInteger(num);
	return formatMoney(amount, isWhole ? 0 : 2, ".", thousands);
}

/**
 * Yorliq (price tag) uchun: butun sonlar kasrsiz, kasrli narxlar kasr qismi bilan
 * ko'rsatiladi -- yumaloqlanmaydi. formatMoney(x, 0) toFixed(0) qilib 3.6 ni "4"
 * ga aylantirardi; bu esa yorliqda narxni aslicha chiqaradi.
 */
export function formatMoneyAuto(amount) {
	const num = Number(amount);
	if (isNaN(num)) return formatMoney(amount, 0);
	return Number.isInteger(num) ? formatMoney(num, 0) : formatMoney(num, 2);
}

export function formatDateWithTime(date) {
	try {
		if (date) {
			var formattedDate = format(new Date(date), 'dd.MM.yyyy HH:mm');
			return formattedDate
		}
	} catch (error) {
		return date
	}
}

export function formatDate(date) {
	try {
		if (date) {
			var formattedDate = format(new Date(date), 'dd.MM.yyyy');
			return formattedDate
		}
	} catch (error) {
		return date;
	}
}

export function formatDateBackend(date) {
	try {
		if (date) {
			var formattedDate = format(new Date(date), 'yyyy-MM-dd');
			return formattedDate
		}
	} catch (error) {
		return date
	}
}

export function getUnixTime() {
	return getTime(new Date())
}

export function todayDate() {
	return format(new Date(), 'dd.MM.yyyy HH:mm:ss')
}

export function todayDDMMYYYY() {
	return format(new Date(), 'dd.MM.yyyy')
}

export function getHHmm() {
	return format(new Date(), 'HH:mm')
}

export function getUnixTimeByDate(date) {
	return getTime(date)
}

export function formatUnixTime(unixTime) {
	if (unixTime)
		return format(new Date(unixTime), 'dd.MM.yyyy HH:mm:ss')
}

export function checkRole(role) {
	const roles = store.getState()?.account.authorities;
	var item = roles?.find(item => item === role)
	if (item?.length > 0) return true
	return false;
}